// SavedView — the platform "saved views" primitive.
//
// Per CLAUDE.md Iron Rule #11, every entity browser (Intel page today;
// Vendor / Agency / Award maps tomorrow) consumes this component. There is
// no per-page saved-view UI.
//
// Anatomy: a small trigger button — "VIEWS" + active view name + caret —
// opens an absolutely-positioned dropdown that lists every saved view for
// the entity, plus an inline "Save current view" composer.
//
// State the host owns: current FilterState, current view name (optional),
// and a callback to load a FilterState into URL search params. Everything
// else (list / create / delete / alert toggle) flows through the bundled
// TanStack Query hooks.

import type { filters as f } from '@eidollon/contracts';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { SavedViewItem } from './SavedViewItem.js';
import type { SavedViewEndpoints } from './types.js';
import { useAoiAlertToggle } from './useAoiAlertToggle.js';
import { type SavedViewFetcher, useSavedViews } from './useSavedViews.js';

export interface SavedViewProps {
  /** Entity identifier (matches `FilterSchema.entity`). Scopes the cache. */
  entity: string;
  /** Current FilterState — used as the body of "Save current view". */
  filters: f.FilterState;
  /** Load callback: when a row is clicked, host writes this into URL state. */
  onLoad: (next: f.FilterState) => void;
  /** Auth-aware fetcher; the host wires JWT/cookies. */
  fetcher: SavedViewFetcher;
  /** Optional override for the REST endpoints. Defaults are good. */
  endpoints?: SavedViewEndpoints;
  /** Currently-loaded view id (highlighted in the list). */
  activeId?: string | null;
}

export function SavedView({
  entity,
  filters,
  onLoad,
  fetcher,
  endpoints,
  activeId,
}: SavedViewProps) {
  const [open, setOpen] = useState(false);
  const [composing, setComposing] = useState(false);
  const [name, setName] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const composerInputRef = useRef<HTMLInputElement>(null);

  // Focus the composer input the moment the user clicks "+ SAVE CURRENT".
  // Avoids the `autoFocus` prop (a11y rule disallows it page-wide; this is a
  // user-initiated, popover-scoped focus call so it's the right pattern).
  useEffect(() => {
    if (composing) composerInputRef.current?.focus();
  }, [composing]);

  const { list, create, remove } = useSavedViews({
    entity,
    fetcher,
    ...(endpoints ? { endpoints } : {}),
  });
  const aoiToggle = useAoiAlertToggle({ entity, fetcher, ...(endpoints ? { endpoints } : {}) });

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target as Node)) return;
      setOpen(false);
      setComposing(false);
      setName('');
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const items = list.data ?? [];
  const activeItem = activeId ? items.find((i) => i.id === activeId) : undefined;

  const handleCreate = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    create.mutate(
      { name: trimmed, filters },
      {
        onSuccess: () => {
          setComposing(false);
          setName('');
        },
      },
    );
  };

  const handleDelete = (id: string) => {
    setPendingDeleteId(id);
    remove.mutate(id, {
      onSettled: () => {
        setPendingDeleteId((cur) => (cur === id ? null : cur));
      },
    });
  };

  return (
    <div
      ref={rootRef}
      data-component="SavedView"
      style={{ position: 'relative', display: 'inline-flex' }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="saved views"
        title="saved views"
        style={triggerStyle}
      >
        <span style={{ color: 'var(--fg-3)', letterSpacing: 0.4 }}>VIEWS</span>
        <span
          style={{
            color: 'var(--fg-1)',
            maxWidth: 140,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {activeItem?.name ?? '—'}
        </span>
        <span style={{ color: 'var(--fg-3)' }}>▾</span>
      </button>

      {open && (
        <div role="menu" aria-label="saved views" style={menuStyle}>
          <div style={menuHeaderStyle}>
            <span style={{ color: 'var(--fg-3)', fontSize: 10, letterSpacing: 0.4 }}>
              SAVED VIEWS · {entity}
            </span>
            <button
              type="button"
              onClick={() => setComposing((c) => !c)}
              style={addButtonStyle}
              title="save the current filter state as a new view"
            >
              + SAVE CURRENT
            </button>
          </div>

          {composing && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCreate();
              }}
              style={composerStyle}
            >
              <input
                ref={composerInputRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="view name"
                aria-label="new saved view name"
                maxLength={120}
                style={composerInputStyle}
              />
              <button
                type="submit"
                disabled={!name.trim() || create.isPending}
                style={composerSubmitStyle}
              >
                SAVE
              </button>
              {create.isError && (
                <span role="alert" style={{ color: 'var(--red, #ff3366)', fontSize: 10 }}>
                  {create.error?.message ?? 'save failed'}
                </span>
              )}
            </form>
          )}

          <div style={listStyle}>
            {list.isLoading && <div style={emptyRowStyle}>loading…</div>}
            {list.isError && !list.isLoading && (
              <div style={{ ...emptyRowStyle, color: 'var(--red, #ff3366)' }}>
                {list.error?.message ?? 'failed to load'}
              </div>
            )}
            {!list.isLoading && !list.isError && items.length === 0 && (
              <div style={emptyRowStyle}>(no saved views)</div>
            )}
            {items.map((s) => (
              <SavedViewItem
                key={s.id}
                id={s.id}
                name={s.name}
                description={s.description ?? null}
                polygonCount={s.aoi_polygon_count}
                alertActive={s.aoi_alert_active}
                pending={
                  pendingDeleteId === s.id ||
                  (aoiToggle.isPending && aoiToggle.variables?.savedScreenId === s.id)
                }
                onLoad={() => {
                  onLoad(s.filters);
                  setOpen(false);
                }}
                onDelete={() => handleDelete(s.id)}
                onAlertToggle={(active) => aoiToggle.mutate({ savedScreenId: s.id, active })}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 *  Styles — keep visuals in this file so consumers get the dense theme by
 *  default. CSS variables come from `apps/terminal/src/styles/tokens.css`
 *  (graduating to `packages/ui/styles/` per the orchestrator plan).
 * ────────────────────────────────────────────────────────────────────────── */

const triggerStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 22,
  padding: '0 8px',
  background: 'transparent',
  border: '1px solid var(--bd-strong)',
  cursor: 'pointer',
  fontFamily: 'var(--mono)',
  fontSize: 11,
  color: 'var(--fg-1)',
};

const menuStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  right: 0,
  minWidth: 280,
  maxWidth: 360,
  zIndex: 30,
  background: 'var(--bg-pane-2, #141414)',
  border: '1px solid var(--bd-strong)',
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
  fontFamily: 'var(--mono)',
};

const menuHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: 24,
  padding: '0 8px',
  borderBottom: '1px solid var(--bd)',
  background: 'var(--bg-pane)',
  fontFamily: 'var(--mono)',
};

const addButtonStyle: CSSProperties = {
  background: 'transparent',
  border: '1px dashed var(--bd-strong)',
  cursor: 'pointer',
  color: 'var(--fg-2)',
  fontFamily: 'var(--mono)',
  fontSize: 10,
  letterSpacing: 0.4,
  height: 18,
  padding: '0 6px',
};

const composerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 8px',
  borderBottom: '1px solid var(--bd)',
  background: 'var(--bg-pane)',
};

const composerInputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  height: 22,
  padding: '0 6px',
  background: 'var(--bg-base)',
  border: '1px solid var(--bd-strong)',
  color: 'var(--fg-1)',
  fontFamily: 'var(--mono)',
  fontSize: 11,
};

const composerSubmitStyle: CSSProperties = {
  height: 22,
  padding: '0 8px',
  background: 'var(--primary-soft, rgba(52,120,246,0.15))',
  border: '1px solid var(--primary, #3478f6)',
  color: 'var(--primary, #3478f6)',
  cursor: 'pointer',
  fontFamily: 'var(--mono)',
  fontSize: 10,
  letterSpacing: 0.4,
};

const listStyle: CSSProperties = {
  maxHeight: 280,
  overflowY: 'auto',
  padding: '4px 0',
};

const emptyRowStyle: CSSProperties = {
  padding: '8px 10px',
  color: 'var(--fg-3)',
  fontFamily: 'var(--mono)',
  fontSize: 11,
};
